import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import HowItWorksSection from "../landing/sections/HowItWorksSection.tsx";

export const Route = createFileRoute('/tournament-management-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "FX", title: "Fixture generation", desc: "Create schedules for different tournament formats in minutes."},
    {code: "LS", title: "Live scoring", desc: "Collect scores quickly and keep results visible throughout the event."},
    {code: "ST", title: "Standings and rankings", desc: "Track points, wins, losses and positions as matches are completed."},
    {code: "PL", title: "Player management", desc: "Manage participants, teams, divisions and tournament communication."},
    {code: "CT", title: "Court scheduling", desc: "Organize matches across courts, time slots and event sessions."},
    {code: "PB", title: "Public results", desc: "Publish fixtures, scores and standings for players and spectators."},
];

const BENEFITS = [
    {title: "Create tournaments faster", desc: "Set up formats, players and fixtures without manual spreadsheet work."},
    {title: "Reduce admin during events", desc: "Keep scores, standings and match progress organized in one place."},
    {title: "Improve the player experience", desc: "Give players a clear view of fixtures, standings and next matches."},
];

const PROBLEMS = [
    "Fixtures are hard to generate manually",
    "Players keep asking who they play next",
    "Scores are scattered across messages",
    "Standings are slow to update",
    "Late changes break the schedule",
    "Final results are hard to publish clearly",
];

const FORMATS = [
    {name: "Round Robin", sub: "Fair rankings and leagues", to: "/round-robin-tournament-generator/"},
    {name: "Single Elimination", sub: "Fast knockout brackets", to: "/single-elimination-bracket-generator/"},
    {name: "Double Elimination", sub: "Second-chance brackets", to: "/double-elimination-bracket-generator/"},
    {name: "Group and Knockout", sub: "Groups into finals", to: "/group-and-knockout-generator/"},
    {name: "Americano", sub: "Rotating doubles events", to: "/americano-generator/"},
    {name: "Mexicano", sub: "Adaptive social doubles", to: "/mexicano-tournament-generator/"},
    {name: "Swiss System", sub: "Large competitive events", to: "/swiss-system-tournament-generator/"},
    {name: "Ladder League", sub: "Ongoing club rankings", to: "/ladder-league-generator/"},
];

const SPORTS = [
    {title: "Padel tournament software", desc: "Run padel Americano, Mexicano, leagues and club championships.", to: "/padel-tournament-software/"},
    {title: "Pickleball tournament software", desc: "Create pickleball brackets, round robins and ladder events.", to: "/pickleball-tournament-software/"},
    {title: "Tennis tournament software", desc: "Manage tennis fixtures, courts, rankings and live results.", to: "/tennis-tournament-software/"},
    {title: "League management software", desc: "Organize recurring leagues, tables, standings and player results.", to: "/sports-league-management-software/"},
    {title: "Live scoring app", desc: "Collect scores and publish live results and standings as you play.", to: "/live-scoring/"},
    {title: "Tournament format guides", desc: "Compare formats and choose the right structure for your event.", to: "/tournament-formats/"},
];

const COMPARE = [
    {need: "Generate fixtures", manual: "Manual formulas and editing", scorepal: "Built-in tournament structures"},
    {need: "Collect scores", manual: "Messages, forms or paper", scorepal: "Centralized score tracking"},
    {need: "Update standings", manual: "Manual updates", scorepal: "Clear live standings and results"},
    {need: "Player experience", manual: "Players ask organizers for updates", scorepal: "Players can see fixtures and results"},
    {need: "Event admin", manual: "High admin load", scorepal: "Less manual work for organizers"},
];

const FAQS = [
    {
        q: 'What is tournament management software?',
        a: <>Tournament management software helps organizers create fixtures, manage players, record scores, update standings and publish results from one platform.</>,
    },
    {
        q: 'Can ScorePal generate fixtures?',
        a: <>Yes. ScorePal creates fixtures for different tournament formats including <Link to={"/round-robin-tournament-generator/" as any} className="text-[#082F8A] underline font-semibold">round robin</Link>, knockout, <Link to={"/group-and-knockout-generator/" as any} className="text-[#082F8A] underline font-semibold">group stages</Link> and rotating doubles formats.</>,
    },
    {
        q: 'What sports can use ScorePal?',
        a: <>ScorePal can be used for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby and many other club or tournament sports.</>,
    },
    {
        q: 'Is ScorePal only for large tournaments?',
        a: <>No. ScorePal supports small club nights, recurring leagues, social events, competitive tournaments and larger multi-division events.</>,
    },
    {
        q: 'Can players see fixtures and results?',
        a: <>Yes. ScorePal makes fixtures, scores and standings easy for organizers and players to follow.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Tournament Management Software for Clubs & Organizers | ScorePal</title>
            <meta name="description" content="Run tournaments from one platform. ScorePal helps organizers create fixtures, manage registrations, track live scores, publish standings and run tournaments for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby, esports and more." />
            <link rel="canonical" href={`${config.publicUrl}/tournament-management-software/`} />
            <meta property="og:title" content="Tournament Management Software | ScorePal" />
            <meta property="og:description" content="Create fixtures, manage players, track scores and publish live tournament standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/tournament-management-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/tournament-management-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Tournament Management Software | ScorePal" />
            <meta name="twitter:description" content="Run tournaments, fixtures, live scores and standings from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Tournament Management Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Tournament management software for creating fixtures, managing registrations, tracking live scores and publishing standings.",
                "url": "https://scorepal.co/tournament-management-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is tournament management software?", "acceptedAnswer": {"@type": "Answer", "text": "Tournament management software helps organizers create fixtures, manage players, collect scores, publish standings and run tournaments from one platform."}},
                    {"@type": "Question", "name": "What tournament formats does ScorePal support?", "acceptedAnswer": {"@type": "Answer", "text": "ScorePal supports tournament formats including round robin, knockout, group stage, Americano, Mexicano, ladder leagues and club competitions."}},
                    {"@type": "Question", "name": "Can ScorePal be used for padel, tennis and pickleball?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal is suitable for padel, tennis, pickleball, football, basketball, baseball, cricket, rugby and other sports tournaments."}},
                    {"@type": "Question", "name": "Is ScorePal only for large tournaments?", "acceptedAnswer": {"@type": "Answer", "text": "No. ScorePal supports small club nights, recurring leagues, social events, competitive tournaments and larger multi-division events."}},
                    {"@type": "Question", "name": "Can players see fixtures and results?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal makes fixtures, scores and standings easy for organizers and players to follow."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Tournament Management Software", item: "https://scorepal.co/tournament-management-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Tournament Management Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Run better tournaments with less admin
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps clubs, leagues and organizers create fixtures, manage players, collect scores,
                                    publish standings and run tournaments from one simple platform.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/contact/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Explore Formats
                                    </Link>
                                </div>

                                <div className="flex flex-wrap gap-2.5 mt-8">
                                    {["Padel", "Tennis", "Football", "Basketball", "Baseball", "Cricket", "Rugby", "Esports"].map((s) => (
                                        <span key={s} className="rounded-full bg-white/10 px-3.5 py-1.5 font-inter text-sm font-medium text-white/80">{s}</span>
                                    ))}
                                </div>
                            </div>

                            {/* Dashboard card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Everything your tournament needs</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">From setup to final standings, keep players, matches and scores organized.</p>
                                <div className="space-y-3">
                                    {[
                                        {label: "Fixtures", value: "Generated"},
                                        {label: "Live Scores", value: "In progress"},
                                        {label: "Standings", value: "Auto-updated"},
                                        {label: "Finals", value: "Ready"},
                                    ].map((row) => (
                                        <div key={row.label} className="flex items-center justify-between rounded-xl bg-white/10 px-4 py-3.5">
                                            <strong className="font-inter text-white text-sm">{row.label}</strong>
                                            <span className="font-inter text-white/70 text-sm">{row.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Tournament management without spreadsheets
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace manual brackets, WhatsApp messages and spreadsheet formulas with a platform built for sports organizers.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits + problems */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Built for organizers who need speed and control
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            ScorePal helps you move from planning to live play faster, while still giving you control over
                            formats, players, scoring and schedules.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white border border-[#e5e7eb] rounded-2xl p-6 lg:p-8 shadow-sm">
                        <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-4">Common organizer problems ScorePal helps solve</h3>
                        <ul className="list-disc pl-5 space-y-2.5 font-inter text-sm text-[#1a1a2e]/80">
                            {PROBLEMS.map((p) => <li key={p}>{p}</li>)}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Formats */}
            <section id="formats" className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every popular tournament format
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Whether you are running a competitive bracket, a social club night or a full group-stage event,
                        ScorePal helps you organize the structure.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {FORMATS.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* How ScorePal works (shared landing-page section) */}
            <HowItWorksSection />

            {/* Sports / related */}
            <section id="sports" className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Tournament software for every club sport
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        ScorePal runs racquet sports, team sports and esports alike — from padel, tennis and pickleball
                        to football, basketball, baseball, cricket and rugby, plus social competitions and leagues.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {SPORTS.map((s) => (
                            <Link key={s.title} to={s.to as any} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{s.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{s.desc}</p>
                            </Link>
                        ))}
                    </div>
                    <div className="mt-8">
                        <p className="font-inter text-sm font-semibold text-[#1a1a2e]/60 mb-3">Also great for</p>
                        <div className="flex flex-wrap gap-2.5">
                            {["Football", "Basketball", "Baseball", "Cricket", "Rugby", "Esports", "Squash", "Badminton"].map((s) => (
                                <span key={s} className="rounded-full border border-[#e5e7eb] bg-[#F6F7FB] px-4 py-2 font-inter text-sm font-medium text-[#1a1a2e]/80">{s}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* From generators to operations CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            From free generators to full tournament operations
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            Use ScorePal's tournament generators to plan your event, then move into the full platform when you
                            need registrations, live scoring, standings and communication.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/contact/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">Get started - it's free</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/tournament-formats/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                View Free Tools
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Best for</h3>
                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-white/80">
                            <li>Club tournaments</li>
                            <li>League organizers</li>
                            <li>Padel and tennis clubs</li>
                            <li>Pickleball groups</li>
                            <li>Corporate sports events</li>
                            <li>School competitions</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Tournament management software comparison
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        ScorePal is designed for organizers who want a modern, simple way to run tournaments without stitching together spreadsheets, forms and chat groups.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Manual spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Ready to run your next tournament?
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Create fixtures, manage players, track scores and publish results with tournament management software built for modern sports organizers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Fixtures", "Live Scores", "Standings", "Player Rankings", "Tournament Formats", "Club Events"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/contact/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Tournament Management Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
